import React, { useMemo } from "react";
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	TextField,
	Tooltip,
} from "@mui/material";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IB2BSession } from "src/Types/module/b2bSession/b2bSession.types";
import dayjs from "dayjs";

interface Props {
	label?: string;
	value: number[];
	onChange: (sessionIds: number[]) => void;
	error?: boolean;
	helperText?: string;
	required?: boolean;
}

const formatDisplayTime = (timeStr?: string) => {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	if (hours === undefined || minutes === undefined) return timeStr;
	const dummyDate = dayjs().hour(Number(hours)).minute(Number(minutes));
	return dummyDate.format("hh:mm A");
};

const SessionMultiSelectField: React.FC<Props> = ({
	label = "Select Session(s) *",
	value = [],
	onChange,
	error,
	helperText,
	required,
}) => {
	const { data: rawSessions = [], isLoading } = useApiQuery<IB2BSession[]>({
		endPoint: "/api/fair/admin/sessions",
		queryKeys: ["B2BSessions"],
	});

	const sessions: IB2BSession[] = useMemo(() => {
		if (Array.isArray(rawSessions)) return rawSessions;
		if (Array.isArray((rawSessions as any)?.data)) return (rawSessions as any).data;
		return [];
	}, [rawSessions]);

	const selectedSessions = useMemo(() => {
		return sessions.filter((s) => value.includes(s.id));
	}, [sessions, value]);

	const getOptionTitle = (s: IB2BSession) => {
		const dateFormatted = s.date ? dayjs(s.date).format("DD MMM, YYYY") : "";
		const timeSlot = `${formatDisplayTime(s.start_time)} – ${formatDisplayTime(s.end_time)}`;
		return `${s.session_no || `Session ${s.id}`} • ${dateFormatted} (${timeSlot})`;
	};

	const handleToggleSession = (sessionId: number) => {
		if (value.includes(sessionId)) {
			onChange(value.filter((id) => id !== sessionId));
		} else {
			onChange([...value, sessionId]);
		}
	};

	return (
		<Box>
			{/* Autocomplete Input */}
			<Autocomplete<IB2BSession, true, false, false>
				multiple
				options={sessions}
				value={selectedSessions}
				loading={isLoading}
				getOptionLabel={(option) =>
					typeof option === "string" ? option : getOptionTitle(option)
				}
				isOptionEqualToValue={(option, val) => option.id === val.id}
				onChange={(_, newValue) => {
					onChange(newValue.map((item) => item.id));
				}}
				renderTags={(tagValue, getTagProps) =>
					tagValue.map((option, index) => (
						<Chip
							label={option.session_no || `Session ${option.id}`}
							size="small"
							color="primary"
							variant="outlined"
							{...getTagProps({ index })}
							key={option.id}
						/>
					))
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						size="small"
						placeholder={
							selectedSessions.length === 0 ? "Select session(s)..." : ""
						}
						error={error || (required && selectedSessions.length === 0)}
						helperText={helperText}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={18} />
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>

			{/* Compact quick-select chips directly below the field */}
			{sessions.length > 0 && (
				<Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
					{sessions.map((s) => {
						const isSelected = value.includes(s.id);
						const dateFormatted = s.date ? dayjs(s.date).format("DD MMM") : "";
						const timeSlot = `${formatDisplayTime(s.start_time)} - ${formatDisplayTime(s.end_time)}`;
						const tooltipText = `${s.session_no || `Session ${s.id}`} • ${dateFormatted} (${timeSlot})`;

						return (
							<Tooltip title={tooltipText} key={s.id} arrow>
								<Chip
									label={s.session_no || `Session ${s.id}`}
									size="small"
									clickable
									onClick={() => handleToggleSession(s.id)}
									color={isSelected ? "primary" : "default"}
									variant={isSelected ? "filled" : "outlined"}
									sx={{
										fontSize: "0.75rem",
										fontWeight: isSelected ? 700 : 500,
										cursor: "pointer",
									}}
								/>
							</Tooltip>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export default SessionMultiSelectField;
